import { ReactElement } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Stack } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { CoordinatePointInput } from '@ui/forms'
import { useStoreSubscription } from '@editorHooks'
import LengthAdjustmentFields from './LengthAdjustmentFields'
import XYZTranslationFields from './XYZTranslationFields'

type InteractionMode = '2D' | '3D'

interface Props {
  updateElementMutation: ({
    guid,
    shape: { start, end },
  }: {
    guid: string
    shape: { start: ImmutableVector3; end: ImmutableVector3 }
  }) => void
  interactionMode: InteractionMode
}

const LineGeometryFormFields = ({
  updateElementMutation,
  interactionMode,
}: Props): ReactElement => {
  const { setValue } = useFormContext()
  useStoreSubscription({
    writeCallback: ({ guid, start, end }) =>
      updateElementMutation({
        guid,
        shape: {
          start: new ImmutableVector3(start.x, start.y, start.z),
          end: new ImmutableVector3(end.x, end.y, end.z),
        },
      }),
  })

  const switchXY: boolean = useWatch({ name: 'xy_decimal_points_rounding' })

  const handleReplaceStart2D = (x: number, y: number) => {
    setValue('start.x', x)
    setValue('start.y', y)
  }
  const handleReplaceEnd2D = (x: number, y: number) => {
    setValue('end.x', x)
    setValue('end.y', y)
  }

  const handleReplaceStart3D = (x: number, y: number, z: number) => {
    setValue('start.x', x)
    setValue('start.y', y)
    setValue('start.z', z)
  }
  const handleReplaceEnd3D = (x: number, y: number, z: number) => {
    setValue('end.x', x)
    setValue('end.y', y)
    setValue('end.z', z)
  }

  const handleReplaceStart = (x: number, y: number, z: number) => {
    if (interactionMode === '2D') {
      handleReplaceStart2D(x, y)
    } else {
      handleReplaceStart3D(x, y, z)
    }
  }
  const handleReplaceEnd = (x: number, y: number, z: number) => {
    if (interactionMode === '2D') {
      handleReplaceEnd2D(x, y)
    } else {
      handleReplaceEnd3D(x, y, z)
    }
  }

  return (
    <Stack spacing={1}>
      <CoordinatePointInput
        name="start"
        label="Start"
        pointManipulation={{
          x: { roundOnReplace: switchXY },
          y: { roundOnReplace: switchXY },
        }}
        onReplace={handleReplaceStart}
      />

      <CoordinatePointInput
        name="end"
        label="End"
        pointManipulation={{
          x: { roundOnReplace: switchXY },
          y: { roundOnReplace: switchXY },
        }}
        onReplace={handleReplaceEnd}
      />
      <LengthAdjustmentFields />
      <XYZTranslationFields />
    </Stack>
  )
}

export default LineGeometryFormFields
